import React, { Component, PropTypes } from 'react';
import shouldPureComponentUpdate from 'react-pure-render/function';
import FauxSelect from 'components/FauxSelect/FauxSelect';
import BookSelect from 'components/BookSelect/BookSelect';
import calendarUtils from 'utils/calendar';
import dates from 'utils/dates';
import { round, dayInMilliseconds, range, isCurrencyEur } from 'utils/utils';
import * as optimizelyUtil from 'utils/optimizely';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import config from 'config/global';
import CalDate from './Date';

import './PandaCalendar.scss';

export default class PandaCalendar extends Component {
  static propTypes = {
    annualAvailability: ImmutablePropTypes.map.isRequired,
    appState: ImmutablePropTypes.map.isRequired,
    baseState: ImmutablePropTypes.map.isRequired,
    disableFooter: PropTypes.bool,
    disableHeader: PropTypes.bool,
    disableIcons: PropTypes.bool,
    featureFlags: ImmutablePropTypes.map.isRequired,
    isDP: PropTypes.bool,
    isMobileUI: PropTypes.bool,
    isPriceInPence: PropTypes.bool,
    noResults: PropTypes.bool,
    numberOfChargeablePassengers: PropTypes.number,
    numberOfNights: PropTypes.number,
    offer: PropTypes.object.isRequired,
    onClickDate: PropTypes.func.isRequired,
    onlyHighlightSelectedDate: PropTypes.bool,
    onPageChange: PropTypes.func,
    showPricePerNight: PropTypes.bool,
  };

  static defaultProps = {
    annualAvailability: Immutable.Map(),
    appState: Immutable.Map(),
    baseState: Immutable.Map(),
    disableFooter: false,
    disableHeader: false,
    disableIcons: false,
    isDP: false,
    isMobileUI: false,
    offer: Immutable.Map(),
    onClickDate: false,
    onlyHighlightSelectedDate: false,
    showPricePerNight: false,
  };

  shouldComponentUpdate = shouldPureComponentUpdate;

  getCalendarDatesArray(selectedYear, selectedMonth) {
    const monthDays = [];
    const firstOfSelectedMonth = new Date(selectedYear, selectedMonth, 1);
    const monthLength = dates.numDaysInMonth(selectedYear, selectedMonth);

    const dayOfWeekOfFirst = firstOfSelectedMonth.getDay();
    // take a day off before getting dayOfWeek so that monday=0
    const dayOfWeek = dayOfWeekOfFirst === 0 ? 6 : (dayOfWeekOfFirst - 1);

    for (let i = 0; i < monthLength; i++) {
      monthDays[i] = {
        day: i + 1,
        year: selectedYear,
        month: selectedMonth,
      };
    }

    for (let i = 0; i < dayOfWeek; i++) {
      monthDays.unshift(null);
    }

    const remainingDates = 7 - (monthDays.length % 7);

    if (remainingDates !== 7) {
      for (let i = 0; i < remainingDates; i++) {
        monthDays.push(null);
      }
    }

    return monthDays;
  }

  getBlankCalendarDatesArray() {
    const monthDays = [];
    const rows = 5;
    const numDays = 7 * rows;

    for (let i = 0; i < numDays; i++) {
      monthDays.push(null);
    }

    return monthDays;
  }

  poundsToPence(price) {
    return price && price * 100;
  }

  perPersonToTotalPrice(price) {
    const { numberOfChargeablePassengers } = this.props;
    return price * numberOfChargeablePassengers;
  }

  attachPricesAndHighlightsToMonth(datesArray, monthWithPrices, departureTime) {
    const {
      isPriceInPence,
      numberOfNights,
      offer,
      onlyHighlightSelectedDate,
      showPricePerNight,
    } = this.props;

    const multipleDaysInMs = dayInMilliseconds * numberOfNights;

    datesArray.map((date) => {
      if (date) {
        date.price = monthWithPrices.get(`${date.day}`);

        date.highlight = '';

        if (!date.price) {
          date.highlight = 'unavailable';
        } else if (!isPriceInPence) {
          // we need the price in pence
          date.price = this.poundsToPence(date.price);
        }

        if (showPricePerNight) {
          date.price = this.perPersonToTotalPrice(date.price);
        }

        const theDate = new Date(Date.UTC(date.year, date.month, date.day));
        const iterationTime = theDate.getTime();
        const returnTime = departureTime + multipleDaysInMs;

        if ((iterationTime > departureTime && iterationTime < returnTime) && !onlyHighlightSelectedDate) {
          date.highlight = 'selected';
        } else if (iterationTime === departureTime) {
          date.highlight = onlyHighlightSelectedDate ? 'checkin' : 'depart';

          // use the offer price if it's available
          const discountPrice = offer.get('discountPrice', false);
          const discountPriceInPence = isPriceInPence ? discountPrice : this.poundsToPence(discountPrice);
          date.price = discountPrice ? discountPriceInPence : date.price;
        } else if (iterationTime === returnTime && !onlyHighlightSelectedDate) {
          date.highlight = 'return';
        }
      }
    });
  }

  getMonthAvailabilityObject(baseStateTime) {
    let datesArray = this.getBlankCalendarDatesArray();

    if (baseStateTime) {
      const selectedDate = new Date(baseStateTime);
      const selectedMonth = selectedDate.getMonth();
      const selectedYear = selectedDate.getFullYear();

      const annualAvailabilityMonth = this.props.annualAvailability
        .getIn([`${selectedYear}`, `${selectedMonth}`]);

      if (annualAvailabilityMonth) {
        datesArray = this.getCalendarDatesArray(selectedYear, selectedMonth);
        this.attachPricesAndHighlightsToMonth(datesArray, annualAvailabilityMonth, baseStateTime);

        this.flagCheapestOfMonth(datesArray);
      }
    }

    return datesArray;
  }

  flagCheapestOfMonth(datesArray) {
    const arrayOfPrices = datesArray
      .filter(date => !!date)
      .map(date => round(date.price))
      .filter(price => !isNaN(price));

    const cheapestPrice = Math.min.apply(null, arrayOfPrices);

    datesArray.map((date) => {
      if (date && cheapestPrice === round(date.price)) {
        date.cheapest = true;
      }
    });
  }

  getCheapestDayOfMonth(monthObj) {
    let cheapestDay;
    let cheapestPrice;

    monthObj.map((price, day) => {
      if (!cheapestPrice || price < cheapestPrice) {
        cheapestPrice = price;
        cheapestDay = day;
      }
    });

    return cheapestDay;
  }

  getCalDate() {
    const {
      annualAvailability,
      baseState,
      isDP,
    } = this.props;

    const isPanda = true;

    return calendarUtils.getDepartureDate(baseState, isPanda, isDP, annualAvailability);
  }

  getAvailableMonthsCollection() {
    const { annualAvailability } = this.props;
    const yearKeys = annualAvailability.keySeq().toArray();

    const availableMonthsCollection = [];
    const calDateFromBaseState = this.getCalDate() * dayInMilliseconds;
    const calDateMonthAndYearText = dates.format(calDateFromBaseState, 'MMMM YYYY');

    yearKeys.sort().map((year) => {
      const yearObj = annualAvailability.get(`${year}`, Immutable.List());

      if (yearObj.size) {
        yearObj.map((item, monthNum) => {
          if (item) {
            const cheapestDayOfMonth = this.getCheapestDayOfMonth(item);
            const cheapestOfMonthDate = new Date(Date.UTC(year, monthNum, cheapestDayOfMonth));
            const calDate = cheapestOfMonthDate.getTime() / dayInMilliseconds;
            const monthAndYearText = dates.format(cheapestOfMonthDate, 'MMMM YYYY');

            availableMonthsCollection.push({
              label: monthAndYearText,
              value: calDate,
              checked: monthAndYearText === calDateMonthAndYearText,
            });
          }
        });
      }
    });

    return availableMonthsCollection;
  }

  get currentMonthIndex() {
    const availableMonthsCollection = this.getAvailableMonthsCollection();

    return availableMonthsCollection.findIndex(element => !!element.checked);
  }

  onMonthChange = (event) => {
    const { onClickDate } = this.props;

    onClickDate(event.currentTarget.value);
  };

  handleCalendarInteraction = () => {
    optimizelyUtil.fireEvent('PandaCalendar', 'clicked');
  }

  render() {
    const {
      appState,
      baseState,
      disableFooter,
      disableHeader,
      disableIcons,
      featureFlags,
      isMobileUI,
      noResults,
      numberOfNights,
      onClickDate,
      onPageChange,
      showPricePerNight,
    } = this.props;

    const baseStateTime = this.getCalDate() * dayInMilliseconds;
    const dateCollection = this.getMonthAvailabilityObject(baseStateTime);
    const availableMonthsCollection = this.getAvailableMonthsCollection();
    const currentMonth = availableMonthsCollection[this.currentMonthIndex] || {};

    return (
      <section className="panda-calendar">
        <div className="panda-calendar__inner">
          { isMobileUI ? (
            <div className="panda-calendar__month">
              <FauxSelect
                label={featureFlags.get('PandaCalendarLabelV1') ? 'Change selected date:' : 'Select a date'}
                url="change-month"
                location={location}
                align="center"
                onPageChange={onPageChange}
                disabled={noResults}
              >
                {
                  (!baseStateTime || baseStateTime === config.defaultBridgeDate) ?
                  'Loading' :
                  dates.format(baseStateTime, 'MMMM YYYY')
                }
              </FauxSelect>
            </div>
          ) : (
            <div>
              { !disableHeader && (
                <div>
                  <h3 className="panda-calendar__heading">
                    Selected date
                  </h3>
                  <div className="panda-calendar__month">
                    <BookSelect
                      onFieldChange={this.onMonthChange}
                      label="Month:"
                      value={currentMonth.value}
                      options={availableMonthsCollection}
                      hideLabel
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <table className="panda-calendar__table" onClick={this.handleCalendarInteraction}>
            <thead className="panda-calendar__table__legend">
              <tr>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) =>
                  <th key={index} className="panda-calendar__table__legend__day">{day}</th>,
                )}
              </tr>
            </thead>
            <tbody className="panda-calendar__table__page">
              {range(Math.ceil(dateCollection.length / 7)).map((val, trIndex) => (
                <tr key={trIndex}>
                  {dateCollection.splice(0, 7).map((date, index) => (
                    <CalDate
                      appState={appState}
                      baseState={baseState}
                      date={date}
                      key={index}
                      numberOfNights={numberOfNights}
                      onClickDate={onClickDate}
                      showPricePerNight={showPricePerNight}
                      showPlaneIcon={!disableIcons}
                    />
                    ))}
                </tr>
                ))}
            </tbody>
          </table>
          { !disableFooter && (
            <div className="panda-calendar__footer">
              <span className="panda-calendar__footer__key">{ isCurrencyEur() ? '€' : '£' }</span>
              <span className="panda-calendar__footer__text"> = Cheapest</span>
            </div>
          )}
        </div>
      </section>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/components/PandaCalendar/PandaCalendar.jsx
 **/